import {
  HttpTransportType,
  HubConnectionBuilder,
  LogLevel,
} from '@microsoft/signalr';
import type {
  McsHubConnectionLike,
  McsHubConnector,
  McsSignalRSession,
} from './mello-messenger-send.transport';

const RECONNECT_DELAYS = [0, 2000, 5000, 10000, 30000];

export function withReadStateVersion(hubUrl: string): string {
  return `${hubUrl}${hubUrl.includes('?') ? '&' : '?'}readStateVersion=1`;
}

export function toSameOriginHubUrl(
  hubUrl: string,
  proxyPrefix = '/api/messenger',
  hostOrigin?: string,
): string {
  try {
    const origin =
      hostOrigin ??
      (typeof location !== 'undefined' ? location.origin : 'http://localhost');
    const hostUrl = new URL(origin);
    const isLocalDev = ['localhost', '127.0.0.1'].includes(hostUrl.hostname);
    const url = new URL(hubUrl, origin);
    if (url.origin === origin) return url.href;
    const mcsMatch =
      /^mcs-publicapi-([a-z0-9-]+)\.loandepotdev\.works$/i.exec(url.hostname);
    if (mcsMatch) {
      if (isLocalDev) {
        return `${origin}/api/mcs${url.pathname}${url.search}`;
      }
      return `https://ldapi-${mcsMatch[1].toLowerCase()}.loandepotdev.works/mellomessenger/mcs${url.pathname}${url.search}`;
    }
    if (!isLocalDev) {
      return url.href;
    }
    const marker = '/omni-messenger';
    const index = url.pathname.indexOf(marker);
    if (index !== -1) {
      return `${origin}${proxyPrefix}${url.pathname.slice(index + marker.length)}${url.search}`;
    }
    if (url.hostname.endsWith('loandepotdev.io')) {
      return `${origin}/api/hub${url.pathname}${url.search}`;
    }
    return hubUrl;
  } catch {
    return hubUrl;
  }
}

export class SignalRMcsHubConnector implements McsHubConnector {
  constructor(
    private readonly builderFactory: () => HubConnectionBuilder = () =>
      new HubConnectionBuilder(),
  ) {}

  async connect(
    session: McsSignalRSession,
    refreshSession: () => Promise<McsSignalRSession>,
  ): Promise<McsHubConnectionLike> {
    let initialToken = session.token;
    const connection = this.builderFactory()
      .withUrl(withReadStateVersion(toSameOriginHubUrl(session.hubUrl)), {
        accessTokenFactory: async () => {
          const token = initialToken;
          initialToken = '';
          return token || (await refreshSession()).token;
        },
        transport:
          HttpTransportType.WebSockets | HttpTransportType.ServerSentEvents,
      })
      .withAutomaticReconnect(RECONNECT_DELAYS)
      .configureLogging(LogLevel.Error)
      .build();
    await connection.start();
    return connection;
  }
}
